import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';

import ProviderMap from './ProviderMap';
import { default as ProviderList } from './ProviderList';
import LocationService from '../services/LocationService';
import AnalyticsService from '../../shared/services/AnalyticsService';
import EyeCareProvidersService from '../services/EyeCareProvidersService';
import wideScreenAd1x from '../images/visionworks-wide-sponsored@1x.jpg';
import wideScreenAd2x from '../images/visionworks-wide-sponsored@2x.jpg';
import narrowScreenAd1x from '../images/visionworks-narrow-sponsored@1x.jpg';
import narrowScreenAd2x from '../images/visionworks-narrow-sponsored@2x.jpg';
import SponsoredEcp from './SponsoredEcp';
import Select2WithLabel from '../../shared/components/Select2WithLabel';

// There are only two levels now for zooming. 13 and 10 which is roughly the
// city level (10) and street level (13).
// https://developers.google.com/maps/documentation/javascript/tutorial#zoom-levels
const MAXIMUM_ZOOM = 13;
const DEFAULT_ZOOM = 10;
const resultsLimit = 6;
const distanceOptions = [5, 10, 25, 50, 100];
const metersPerMiles = 1609.34;
const defaultDistanceOption = 5 * metersPerMiles;

// NOTE: this will eventually be dynamic
const PARTNER_URL = 'https://www.visionworks.com/storeLocator?nearby=true';
const PARTNER = 'Visionworks';

/* eslint-disable react/no-string-refs */
// TODO: don't use hardcoded form endpoint
export class Search extends Component {
  constructor(props, context) {
    super(props, context);

    const { el } = this.context;
    const latitude = parseFloat(el.getAttribute('data-latitude'));
    const longitude = parseFloat(el.getAttribute('data-longitude'));

    this.state = {
      userAddress: null,
      geolocation: 'pending',
      mapLoading: false,
      searchSubmitted: false,
      latitude,
      longitude,
      userWithLocation: el.getAttribute('data-user-with-location') === 'true',
      results: [],
      resultsLimit,
      hasResultInNy: null,
      hoveredResultId: null,
      selectedResultId: null,
      currentPage: 0,
      defaultLatitude: latitude,
      defaultLongitude: longitude,
      zoom: parseInt(el.getAttribute('data-zoom'), 10),
      sponsored: el.getAttribute('data-sponsored') === 'true',
      filterOpen: false,
      distanceLimit: defaultDistanceOption,
      selectedDistanceLimit: defaultDistanceOption,
      aboutPdfUrl: el.getAttribute('data-about-pdf-url'),
    };
  }

  componentDidMount() {
    if (this.state.userWithLocation) {
      this.fetchEcps();
    } else {
      this.detectLocation(false);
    }
  }

  setUserCoordinates(mapProps, callback) {
    this.setState(mapProps, callback);
  }

  getAndSetUserCoordinates() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (res, rej) => {
      const address = this.state.userAddress;
      try {
        const { region, latitude, longitude } = await LocationService.geocode(address);
        this.setState(({ hasResultInNy }) => ({
          mapLoading: false,
          hasResultInNy: hasResultInNy || region.short_name === 'NY',
        }));
        this.setUserCoordinates({
          latitude,
          longitude,
          zoom: DEFAULT_ZOOM,
        }, res);

        AnalyticsService.sendSearch(address, latitude, longitude);
      } catch (e) {
        console.error(e);
        this.setState({ mapLoading: false });
        rej(e);
      }
    });
  }

  setPage(page) {
    this.setState({ currentPage: page });
  }

  setHoveredResultId(id) {
    this.setState({ hoveredResultId: id });
  }

  setSelectedResultId(id) {
    this.setState({ selectedResultId: id });
  }

  openResultInfoWindow(id) {
    this.setState((oldState) => {
      const results = oldState.results
        .map((result) => ({ ...result, opened: result.location.id === id }));
      return { ...oldState, results };
    });
  }

  async detectLocation(manually) {
    if (this.state.userWithLocation) {
      this.fetchEcps();
      return;
    }

    try {
      this.setState({ mapLoading: true });
      const location = await LocationService.geolocateUser();
      if (this.state.searchSubmitted && !manually) {
        return;
      }
      const { latitude, longitude } = location.coords;
      this.setState({ latitude, longitude, geolocation: 'complete' }, this.fetchEcps);
    } catch (e) {
      console.error(e);
      this.setState({ geolocation: 'failed', mapLoading: false });
    }
  }

  async fetchEcps() {
    const { latitude, longitude, distanceLimit } = this.state;

    try {
      this.setState({ mapLoading: true });
      const response = await EyeCareProvidersService.getNear({
        latitude,
        longitude,
        distanceLimit,
      });
      const data = await response.json();
      const results = sortBy(data.data, [(location) => !location.promoted, 'distance']);
      this.setState(({ hasResultInNy }) => {
        const newHasResultInNy = hasResultInNy ||
          results.some((ecp) => ecp.location && ecp.location.region === 'NY');

        return {
          results,
          currentPage: 0,
          mapLoading: false,
          selectedResultId: null,
          hasResultInNy: newHasResultInNy,
        };
      }, this.fitBounds);
    } catch (e) {
      console.error(e);
      this.setState({ mapLoading: false });
    }
  }

  fitBounds() {
    if (!this.state.results.length || !LocationService.maps) {
      this.setUserCoordinates({ zoom: DEFAULT_ZOOM });
      return;
    }

    const bounds = this.state.results.map((result) => {
      const { latitude, longitude } = result.location;
      return new LocationService.maps.LatLng({ lat: latitude, lng: longitude });
    });

    // TODO: remove hardcoding and get width dynamically
    // Mobile phone users will thank us
    const { zoom } = LocationService.fitBounds(bounds, { width: 760, height: 280 });

    this.setUserCoordinates({
      zoom: zoom >= MAXIMUM_ZOOM ? MAXIMUM_ZOOM : DEFAULT_ZOOM,
    });

    this.setState({ mapLoading: false });
  }

  async updateUserCoordinatesAndGetEcps() {
    await this.getAndSetUserCoordinates();
    this.fetchEcps();
  }

  submit(e) {
    e.preventDefault();
    const userAddress = e.target.zip.value;
    this.setState({ userAddress, mapLoading: true, searchSubmitted: true }, () => {
      this.updateUserCoordinatesAndGetEcps();
    });
  }

  render() {
    const { t } = this.props;
    const {
      latitude,
      longitude,
    } = this.state;
    const showSponsoredEcp = false; // sponsored || hasResultInNy;
    const partnerUrlWithQuery = latitude && longitude ?
      `${PARTNER_URL}&address=${encodeURIComponent(`${latitude},${longitude}`)}` :
      PARTNER_URL;

    return (
      <div>
        <div className="find-a-doctor__row">
          {
            showSponsoredEcp &&
              <SponsoredEcp href={partnerUrlWithQuery} />
          }
          <section>
            <form
              id="ecp_form"
              className="opt-form find-a-doctor__form"
              action="/find_a_doctor"
              acceptCharset="UTF-8"
              method="post"
              onSubmit={(e) => this.submit(e)}
            >
              <div className="viz-input">
                <input
                  type="text"
                  name="zip"
                  id="zip"
                  className="find-a-doctor__text-input viz-input__input"
                  placeholder=" "
                  required
                />
                <label
                  htmlFor='zip'
                  className="viz-label viz-input__label"
                >
                  {t('search.prefill')}
                </label>
                <button
                  type="submit"
                  className="find-a-doctor__button--search opticon search"
                />
              </div>
              <div className="find-a-doctor__divider">
                <hr className="find-a-doctor__divider__hr" />
                <span className="find-a-doctor__divider__text">
                  {t('search.or')}
                </span>
              </div>
              <input
                type="button"
                value={t('search.searchbar')}
                className="viz-btn viz-btn--secondary viz-btn--sm find-a-doctor__button--nearby"
                onClick={() => {
                  this.detectLocation(true);
                }}
              />
            </form>
          </section>
          <section>
            <ProviderMap
              setUserCoordinates={this.setUserCoordinates.bind(this)}
              setPage={this.setPage.bind(this)}
              setHoveredResultId={this.setHoveredResultId.bind(this)}
              setSelectedResultId={this.setSelectedResultId.bind(this)}
              openResultInfoWindow={this.openResultInfoWindow.bind(this)}
              {...this.state}
            />
          </section>
          {
            showSponsoredEcp &&
              <div>
                <a
                  className="find-a-doctor__banner"
                  href={partnerUrlWithQuery}
                >
                  <img
                    className='find-a-doctor__banner-web'
                    src={wideScreenAd1x}
                    alt={t('search.alt', { partner: PARTNER })}
                    srcSet={`${wideScreenAd1x} 1x, ${wideScreenAd2x} 2x`}
                  />
                  <img
                    className='find-a-doctor__banner-mobile'
                    src={narrowScreenAd1x}
                    alt={t('search.alt', { partner: PARTNER })}
                    srcSet={`${narrowScreenAd1x} 1x, ${narrowScreenAd2x} 2x`}
                  />
                </a>
              </div>
          }
        </div>
        <div className="find-a-doctor__disclaimer">
          <span>{t('search.disclaimer_1')}</span>
          <br />
          <span>{t('search.disclaimer_2')}</span>
        </div>
        <div className="find-a-doctor__row find-a-doctor__row--text-right">
          <strong
            className="find-a-doctor__filter__toggle"
            tabIndex='0'
            role='button'
            onClick={() => {
              this.setState(({ filterOpen }) => ({ filterOpen: !filterOpen }));
            }}
          >
            {t('search.filter_options')}
&nbsp;&nbsp;
            <span className={`opticon caret-${this.state.filterOpen ? 'up' : 'down'}`} />
          </strong>
        </div>
        {
          // TODO: Use a Viz accordion
          this.state.filterOpen &&
            <div className="find-a-doctor__filter">
              <div className="find-a-doctor__row">
                <strong className="find-a-doctor__filter__heading">{t('search.filter_options')}</strong>
                <div className="find-a-doctor__filter__form">
                  <Select2WithLabel
                    selectWrapperClass="viz-select__container find-a-doctor__filter__select"
                    selectName="distanceSelect"
                    selectId="find-a-doctor-distance"
                    selectClass="viz-select__select"
                    data={[...distanceOptions.map((miles) => ({
                      text: t('search.within', { distance: miles }),
                      value: miles * metersPerMiles,
                      key: miles,
                    }))]}
                    value={this.state.selectedDistanceLimit}
                    onSelect={(value) => {
                      this.setState({ selectedDistanceLimit: value });
                    }}
                    labelText={t('search.distance')}
                    labelClass="viz-label viz-select__label"
                  />
                  <button
                    type='button'
                    className="find-a-doctor__filter__submit viz-btn viz-btn--primary viz-btn--grouped viz-btn--sm"
                    onClick={() => {
                      this.setState(({ selectedDistanceLimit }) => ({
                        distanceLimit: selectedDistanceLimit,
                      }), this.fetchEcps);
                    }}
                  >
                    {t('search.apply_filter')}
                  </button>
                </div>
              </div>
            </div>
        }
        <div className="find-a-doctor__row">
          <section>
            <ProviderList
              setPage={this.setPage.bind(this)}
              setUserCoordinates={this.setUserCoordinates.bind(this)}
              setHoveredResultId={this.setHoveredResultId.bind(this)}
              setSelectedResultId={this.setSelectedResultId.bind(this)}
              {...this.state}
            />
          </section>
        </div>
      </div>
    );
  }
}
/* eslint-enable react/no-string-refs */

Search.propTypes = {
  t: PropTypes.func.isRequired,
};

Search.contextTypes = {
  /* eslint-disable react/forbid-prop-types */
  el: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
};

const SearchContainer = withTranslation()(Search);

export default SearchContainer;
